import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type Column,
  createDateRangeFilterValidators,
  DateRenderer,
  EmptyStation,
  type ExplorerDataProvider,
  filterToPostGraphileFilter,
  FilterTypes,
  MessageBar,
  NavigationExplorer,
  sortToPostGraphileOrderBy,
  transformRange,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  type OAuthClientFilter,
  OAuthClientsDocument,
  OAuthClientsOrderBy,
  type OAuthClientsQuery,
  type OAuthClientsQueryVariables,
} from '../../../../generated/axauth-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';

type OAuthClientData = NonNullable<
  OAuthClientsQuery['filtered']
>['nodes'][number];

const AllowUserSignUpRenderer = (value: unknown): JSX.Element => (
  <>{value ? 'Yes' : 'No'}</>
);
const explorerColumns: Column<OAuthClientData>[] = [
  { label: 'Client ID', propertyName: 'id', size: '0.75fr' },
  { label: 'Name', propertyName: 'name' },
  {
    label: 'Allow User Sign Up',
    propertyName: 'allowUserSignUp',
    render: AllowUserSignUpRenderer,
  },
  { label: 'Created At', propertyName: 'createdAt', render: DateRenderer },
  {
    label: 'Last Modified At',
    propertyName: 'updatedAt',
    render: DateRenderer,
  },
];

export const OAuthClients: React.FC = () => {
  const { envId, userStoreId } = useParams<{
    envId: string;
    userStoreId: string;
  }>();

  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);

  // Check the status of axauth-service
  useEffect(() => {
    (async () => {
      const statusMessage = await getManagedServiceAvailabilityMessage(
        ManagedServiceId.AXAUTH_SERVICE,
        envId,
      );
      setServiceStatusMessage(statusMessage);
    })();
  }, [envId]);

  const dataProvider: ExplorerDataProvider<OAuthClientData> = {
    loadData: async ({ pagingInformation, sorting, filters }) => {
      const envApolloClient = environmentApolloClient(
        envId,
        new URL(`/graphql`, config.axAuthAdminBaseUrl).toString(),
      );

      const oAuthClients = await envApolloClient?.query<
        OAuthClientsQuery,
        OAuthClientsQueryVariables
      >({
        query: OAuthClientsDocument,
        variables: {
          condition: {
            idpUserStoreId: userStoreId,
          },
          filter: filterToPostGraphileFilter<OAuthClientFilter>(filters, {
            id: 'includesInsensitive',
            name: 'includesInsensitive',
            allowUserSignUp: 'equalTo',
            createdAt: transformRange,
            updatedAt: transformRange,
          }),
          orderBy: sortToPostGraphileOrderBy(sorting, OAuthClientsOrderBy),
          after: pagingInformation,
        },
        fetchPolicy: 'network-only',
      });

      const data = oAuthClients?.data.filtered?.nodes ?? [];

      return {
        data,
        totalCount: oAuthClients?.data.nonFiltered?.totalCount ?? 0,
        filteredCount: oAuthClients?.data.filtered?.totalCount ?? 0,
        hasMoreData: oAuthClients?.data.filtered?.pageInfo.hasNextPage ?? false,
        pagingInformation: oAuthClients?.data.filtered?.pageInfo.endCursor,
      };
    },
  };

  const [createFromDateFilterValidator, createToDateFilterValidator] =
    createDateRangeFilterValidators<OAuthClientData>();

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="OAuth Clients">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <NavigationExplorer<OAuthClientData>
          title="OAuth Clients"
          stationKey="OAuthClientsExplorer"
          columns={explorerColumns}
          dataProvider={dataProvider}
          calculateNavigateUrl={(item) => {
            return `/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/oAuthClients/${item.id}`;
          }}
          onCreateAction={`/env/${envId}/services/${ManagedServiceId.AXAUTH_SERVICE}/config/userStores/${userStoreId}/oAuthClients/create`}
          defaultSortOrder={{ column: 'updatedAt', direction: 'desc' }}
          filterOptions={[
            {
              label: 'Client ID',
              property: 'id',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Name',
              property: 'name',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Allow User Sign Up',
              property: 'allowUserSignUp',
              type: FilterTypes.Options,
              options: [
                {
                  label: 'Yes',
                  value: true,
                },
                {
                  label: 'No',
                  value: false,
                },
              ],
            },
            {
              label: 'Creation Period (From)',
              property: 'createdAt',
              type: FilterTypes.Date,
              onValidate: createFromDateFilterValidator('createdAt'),
            },
            {
              label: 'Creation Period (To)',
              property: 'createdAt',
              type: FilterTypes.Date,
              onValidate: createToDateFilterValidator('createdAt'),
            },
            {
              label: 'Modification Period (From)',
              property: 'updatedAt',
              type: FilterTypes.Date,
              onValidate: createFromDateFilterValidator('updatedAt'),
            },
            {
              label: 'Modification Period (To)',
              property: 'updatedAt',
              type: FilterTypes.Date,
              onValidate: createToDateFilterValidator('updatedAt'),
            },
          ]}
        />
      )}
    </>
  );
};

export const OAuthClientsCrumb: BreadcrumbResolver = () => 'OAuth Clients';
